import {
  MolsonAutocomplete,
  MolsonBox,
  MolsonInputLabel,
  MolsonSpinner,
  MolsonTextField,
  MolsonTypography
} from '@components/reusable'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { handleCatchError } from '@/utils/functions/data-utils'

// Types
export interface KeycloakOrganization {
  id: string
  name?: string
  title?: string // Keycloak may return 'title' instead of 'name'
  alias?: string
  description?: string
  enabled?: boolean
}

export interface OrganizationOption {
  id: string
  name: string
}

// Response wrapper from Keycloak Organizations API
interface OrganizationsApiResponse {
  customers?: KeycloakOrganization[]
}

export interface OrganizationSwitcherProps {
  /** The current access token from the auth context */
  accessToken?: string
  /** Callback to trigger a full re-authentication after switching organizations */
  onSwitchComplete?: () => void
}

interface TokenExchangeResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

interface DecodedToken {
  organization?: {
    id: string
    name: string
    alias?: string
  }
  [key: string]: unknown
}

// Helper to decode JWT (simple base64 decode, no verification)
const decodeJwt = (token: string): DecodedToken => {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      window
        .atob(base64)
        .split('')
        .map(c => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch {
    return {}
  }
}

const KEYCLOAK_AUTHORITY = import.meta.env.NX_AUTHORITY ?? ''
const CLIENT_ID = import.meta.env.NX_CLIENT_ID ?? ''
const API_BASE_PATH = import.meta.env.NX_API_BASE_PATH ?? ''

// API functions - Organizations are fetched via backend API to avoid CORS issues with Keycloak
const getOrganizations = async (
  accessToken: string
): Promise<KeycloakOrganization[]> => {
  const response = await axios.get<
    OrganizationsApiResponse | KeycloakOrganization[]
  >(`${API_BASE_PATH}/users/Organizations`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json'
    }
  })

  // Handle both wrapped response { customers: [...] } and direct array response
  // @ts-ignore
  const data = response.data.organizations ?? response.data
  if (Array.isArray(data)) {
    return data
  }
  return data.customers ?? []
}

// Token exchange request/response types for backend API
interface BackendTokenExchangeRequest {
  organizationId: string
  refreshToken: string
}

interface BackendTokenExchangeResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
  tokenType: string
}

// Get refresh token from session storage
const getRefreshToken = (): string => {
  const storageKey = `oidc.user:${KEYCLOAK_AUTHORITY}:${CLIENT_ID}`
  const storedData = sessionStorage.getItem(storageKey)

  if (storedData) {
    try {
      const userData = JSON.parse(storedData)
      return userData.refresh_token ?? ''
    } catch {
      return ''
    }
  }
  return ''
}

// Exchange token via backend API (avoids CORS and handles client secret)
const exchangeTokenForOrganization = async (
  organizationId: string,
  accessToken: string
): Promise<TokenExchangeResponse> => {
  const response = await axios.post<BackendTokenExchangeResponse>(
    `${API_BASE_PATH}/users/Organizations/token/exchange`,
    {
      organizationId,
      refreshToken: getRefreshToken()
    } satisfies BackendTokenExchangeRequest,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    }
  )

  // Map backend response to expected format
  return {
    access_token: response.data.accessToken,
    refresh_token: response.data.refreshToken,
    expires_in: response.data.expiresIn,
    token_type: response.data.tokenType
  }
}

const updateSessionStorageTokens = (
  accessToken: string,
  refreshToken: string,
  expiresIn: number
): void => {
  const storageKey = `oidc.user:${KEYCLOAK_AUTHORITY}:${CLIENT_ID}`
  const storedData = sessionStorage.getItem(storageKey)

  if (storedData) {
    try {
      const userData = JSON.parse(storedData)
      userData.access_token = accessToken
      userData.refresh_token = refreshToken

      // Compute absolute expiry so oidc-client-ts doesn't consider the token expired
      // and trigger an unwanted silent renewal on reload
      userData.expires_at = Math.floor(Date.now() / 1000) + expiresIn

      const decoded = decodeJwt(accessToken)
      if (decoded.organization) {
        userData.profile = {
          ...userData.profile,
          organization: decoded.organization
        }
      }

      sessionStorage.setItem(storageKey, JSON.stringify(userData))
    } catch (err) {
      console.error('Failed to update session storage:', err)
    }
  }
}

// Sorting helper
const sortAlphabetically = (
  items: OrganizationOption[]
): OrganizationOption[] => {
  return [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {
      numeric: true,
      sensitivity: 'base'
    })
  )
}

// Get current organization from token
const getCurrentOrganization = (
  accessToken?: string
): { id: string; name: string } | null => {
  if (!accessToken) {
    return null
  }
  const decoded = decodeJwt(accessToken)
  if (decoded.organization) {
    return { id: decoded.organization.id, name: decoded.organization.name }
  }
  return null
}

export const OrganizationSwitcher = ({
  accessToken,
  onSwitchComplete
}: OrganizationSwitcherProps) => {
  const { t } = useTranslation()
  const [selectedOrganization, setSelectedOrganization] =
    useState<OrganizationOption | null>(null)
  const [isSwitching, setIsSwitching] = useState(false)

  const { data: organizations, isLoading } = useQuery({
    queryKey: ['keycloakOrganizations'],
    queryFn: () => getOrganizations(accessToken!),
    enabled: !!accessToken,
    retry: 2,
    staleTime: 5 * 60 * 1000
  })

  const sortedOrganizations = useMemo(() => {
    if (!organizations) {
      return []
    }
    return sortAlphabetically(
      organizations.map(org => ({
        id: org.id,
        name: org.name || org.title || org.id // Handle both 'name' and 'title' fields
      }))
    )
  }, [organizations])

  // Set initial selected organization based on current token
  useEffect(() => {
    if (sortedOrganizations.length > 0 && accessToken) {
      const currentOrg = getCurrentOrganization(accessToken)

      if (currentOrg) {
        const matchingOrg = sortedOrganizations.find(
          org => org.id.toLowerCase() === currentOrg.id.toLowerCase()
        )
        if (matchingOrg) {
          setSelectedOrganization(matchingOrg)
        }
      } else if (!selectedOrganization) {
        setSelectedOrganization(sortedOrganizations[0])
      }
    }
  }, [sortedOrganizations, accessToken, selectedOrganization])

  const handleOrganizationChange = useCallback(
    async (
      _event: React.SyntheticEvent,
      newValue: OrganizationOption | null
    ) => {
      if (!(newValue && accessToken)) {
        return
      }
      if (newValue.id === selectedOrganization?.id) {
        return
      }

      setIsSwitching(true)

      try {
        const tokenResponse = await exchangeTokenForOrganization(
          newValue.id,
          accessToken
        )

        // 1. Persist new tokens to sessionStorage so oidc-client-ts has
        //    the new org's refresh token for the upcoming re-auth flow.
        updateSessionStorageTokens(
          tokenResponse.access_token,
          tokenResponse.refresh_token,
          tokenResponse.expires_in
        )

        // 2. Trigger a full Keycloak redirect (signinRedirect). This is
        //    required because oidc-client-ts needs a complete, consistent
        //    token set (access_token + id_token + refresh_token) all issued
        //    together by Keycloak for the new org. A simple page reload or
        //    signinSilent can't guarantee that — the browser's Keycloak
        //    session cookie or the id_token can still reference the old org.
        //    signinRedirect forces Keycloak to issue a new authorization code,
        //    which oidc-client-ts exchanges for a fully consistent token set.
        if (onSwitchComplete) {
          onSwitchComplete()
        }
        return
      } catch (err) {
        handleCatchError(err)
      } finally {
        setIsSwitching(false)
      }
    },
    [accessToken, selectedOrganization, onSwitchComplete]
  )

  // Don't render if no organizations or still loading
  if (isLoading) {
    return (
      <MolsonBox display="flex" alignItems="center" gap={1}>
        <MolsonSpinner size={20} />
        <MolsonTypography variant="body2" color="text.secondary">
          {t('organizationSwitcher.loading', 'Loading organizations...')}
        </MolsonTypography>
      </MolsonBox>
    )
  }

  if (
    !organizations ||
    organizations.length === 0 ||
    sortedOrganizations.length <= 1
  ) {
    return null
  }

  return (
    <MolsonBox sx={{ width: '100%', maxWidth: 400 }}>
      <MolsonInputLabel sx={{ mb: 1 }}>
        {t('organizationSwitcher.label', 'Organization')}
      </MolsonInputLabel>
      <MolsonAutocomplete<OrganizationOption>
        multiple={false}
        freeSolo={false}
        value={selectedOrganization}
        onChange={(event, newValue) =>
          handleOrganizationChange(
            event,
            typeof newValue === 'string' || Array.isArray(newValue)
              ? null
              : newValue
          )
        }
        options={sortedOrganizations}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.name
        }
        isOptionEqualToValue={(option, value) =>
          option.id.toLowerCase() === value.id.toLowerCase()
        }
        disabled={isSwitching || isLoading}
        loading={isSwitching}
        renderInput={params => (
          <MolsonTextField
            {...params}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isSwitching && <MolsonSpinner size={20} />}
                  {params.InputProps.endAdornment}
                </>
              )
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.id}>
            <MolsonTypography noWrap={true}>{option.name}</MolsonTypography>
          </li>
        )}
      />

      {isSwitching && (
        <MolsonTypography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: 'block' }}
        >
          {t('organizationSwitcher.switching', 'Switching organization...')}
        </MolsonTypography>
      )}
    </MolsonBox>
  )
}
